import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Mail, MoonStar } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import StarField from "@/components/StarField";
import { apiJson, readWebId, saveWebId } from "@/lib/auth";
import { LOGIN } from "@/constants/testIds";
import { useSeo } from "@/lib/seo";
import LOGO_ICON_URL from "@/assets/hero-avatar.webp";

const RESEND_SECONDS = 60;

// Слоты по умолчанию склеены в одну плашку — здесь они разнесены, поэтому скругление форсируем.
const slotClass =
  "h-14 w-11 !rounded-2xl border border-line bg-white/[0.05] text-xl font-semibold text-cream shadow-none sm:w-12";

const LoginPage = () => {
  useSeo({
    title: "Вход | Сказки детям на ночь",
    description: "Оставьте почту, чтобы сделать сказку на сайте и сохранить её на своей полке.",
    path: "/login",
    noindex: true,
  });

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawNext = params.get("next") || "/chat";
  const next = /^\/(?!\/)/.test(rawNext) ? rawNext : "/chat";

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [left, setLeft] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (left <= 0) return undefined;
    timerRef.current = window.setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => window.clearTimeout(timerRef.current);
  }, [left]);

  const requestCode = async (isResend = false) => {
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError("Проверьте адрес почты");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await apiJson("/api/web/auth/request", {
        method: "POST",
        body: JSON.stringify({ email: value, webId: readWebId() || undefined }),
      });
      setStep("code");
      setLeft(RESEND_SECONDS);
      if (isResend) setCode("");
    } catch (e) {
      if (e.status === 429) setError("Слишком много попыток, подождите минуту");
      else setError("Проверьте адрес почты");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value = code) => {
    if (value.length !== 6) {
      setError("Введите 6 цифр из письма");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await apiJson("/api/web/auth/verify", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), code: value }),
      });
      if (data.webId) saveWebId(data.webId);
      navigate(next.startsWith("/chat") ? "/chat?menu=1" : next, { replace: true });
    } catch (e) {
      if (e.status === 429) setError("Слишком много попыток, подождите минуту");
      else if (e.status === 400) setError("Неверный код");
      else setError("Не удалось войти. Попробуйте ещё раз");
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-night-900">
      <div className="absolute inset-0 bg-gradient-to-b from-night-950 via-night-900 to-night-800" />
      <StarField />
      <div className="glow-warm pointer-events-none absolute inset-0" />

      <div className="relative flex flex-1 items-center justify-center px-4 py-12 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md rounded-3xl border border-line bg-white/[0.05] p-7 shadow-lift backdrop-blur-2xl sm:p-9"
        >
          <div className="mb-7 flex flex-col items-center text-center">
            <span className="relative mb-4">
              <span className="absolute -inset-2 rounded-full bg-amber/25 blur-lg" />
              <img
                src={LOGO_ICON_URL}
                alt=""
                className="relative h-14 w-14 rounded-full border border-line object-cover"
              />
            </span>
            <h1 className="font-serif text-3xl text-cream sm:text-4xl">
              {step === "email" ? "Оставьте почту" : "Код из письма"}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {step === "email"
                ? "Пришлём код из шести цифр — пароль придумывать не нужно. Сказки сохранятся на вашей полке."
                : `Отправили код на ${email.trim()}`}
            </p>
          </div>

          {step === "email" ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void requestCode();
              }}
              className="flex flex-col gap-4"
            >
              <label className="block">
                <span className="mb-2 block text-xs font-medium uppercase tracking-[0.16em] text-dim">
                  Ваш email
                </span>
                <span className="relative block">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dim" />
                  <input
                    data-testid={LOGIN.emailInput}
                    data-ym-disable-keys="true"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="mama@example.com"
                    className="h-14 w-full rounded-2xl border border-line bg-white/[0.05] pl-11 pr-4 text-cream placeholder:text-dim focus:border-amber/60 focus:outline-none focus:ring-2 focus:ring-amber/25"
                  />
                </span>
              </label>

              {error ? <p className="text-sm text-destructive">{error}</p> : null}

              <button
                data-testid={LOGIN.requestCodeButton}
                type="submit"
                disabled={busy}
                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-amber px-6 font-semibold text-night-900 shadow-glow transition-all duration-300 hover:bg-amber-hover hover:shadow-glow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <MoonStar className="h-5 w-5" />}
                Получить код
              </button>

              <p className="text-center text-xs leading-relaxed text-dim">
                Нажимая «Получить код», вы соглашаетесь с{" "}
                <a href="/oferta" className="underline underline-offset-2 hover:text-amber-soft">
                  офертой
                </a>{" "}
                и{" "}
                <a href="/privacy" className="underline underline-offset-2 hover:text-amber-soft">
                  политикой конфиденциальности
                </a>{" "}
                и даёте согласие на обработку e-mail для входа. Сказки создаёт искусственный интеллект; данные ребёнка
                (имя, возраст) используются только для сказки.
              </p>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void verify();
              }}
              className="flex flex-col gap-5"
            >
              <div data-testid={LOGIN.codeInput} className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={code}
                  autoFocus
                  inputMode="numeric"
                  onChange={(value) => {
                    setCode(value);
                    if (error) setError("");
                    if (value.length === 6) void verify(value);
                  }}
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className={slotClass} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {error ? <p className="text-center text-sm text-destructive">{error}</p> : null}

              <button
                data-testid={LOGIN.submitButton}
                type="submit"
                disabled={busy}
                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-amber px-6 font-semibold text-night-900 shadow-glow transition-all duration-300 hover:bg-amber-hover hover:shadow-glow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                Войти
                <ArrowRight className="h-4 w-4" />
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setError("");
                  }}
                  className="text-muted-foreground transition-colors hover:text-amber-soft"
                >
                  Другой email
                </button>
                <button
                  data-testid={LOGIN.resendCodeButton}
                  type="button"
                  disabled={left > 0 || busy}
                  onClick={() => void requestCode(true)}
                  className="text-muted-foreground transition-colors hover:text-amber-soft disabled:cursor-not-allowed disabled:text-dim disabled:hover:text-dim"
                >
                  {left > 0 ? `Отправить снова через ${left} с` : "Отправить снова"}
                </button>
              </div>
            </form>
          )}

          <div className="mt-7 border-t border-line pt-5 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-amber-soft"
            >
              <ArrowLeft className="h-4 w-4" />
              Назад на главную
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
